import { Button, Checkbox, Select, Textarea, TextInput } from '@mantine/core';
import { UseFormReturnType as Form } from '@mantine/form';
import { IAutomationSendEmail } from 'pages/revisions-module/template-editor/editors/pipeline/types';
import { useCallback, useMemo, useState } from 'react';
import { useFormInputs } from 'pages/revisions-module/template-editor/editors/form/input/FormInputsDataProvider';
import { useApi } from 'api/api-context';
import { ROLE_ADMIN_ID } from 'model/Role';
import { notifications } from '@mantine/notifications';
import { IconMailForward } from '@tabler/icons-react';

/**
 * Sub-form for send-email automation.
 */
export default function SendEmailSubForm({ form }: { form: Form<IAutomationSendEmail> }) {
  const { allInputs } = useFormInputs();
  const { getAction, roleId } = useApi();
  const isAdmin = roleId === ROLE_ADMIN_ID;
  const [loading, setLoading] = useState(false);

  const sendTestEmail = useCallback(async () => {
    const action = getAction('UserSendTestEmail');
    setLoading(true);

    try {
      await action({
        payload: {
          subject: form.values.subject,
          html: form.values.html,
        },
      });

      notifications.show({
        title: 'Úspech',
        message: 'Testovací e-mail bol odoslaný',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Chyba',
        message: 'Nepodarilo sa odoslať testovací e-mail',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [form.values.subject, form.values.html, getAction]);

  const emailInputs = useMemo(
    () =>
      allInputs
        .filter((input) => input.context === 'revision')
        .map((input) => ({ value: input.spec.name, label: input.spec.label })),
    [allInputs]
  );

  return (
    <>
      <Select
        data={emailInputs}
        label="E-mailové pole"
        placeholder="Vyberte pole s e-mailom"
        required
        searchable
        size="sm"
        {...form.getInputProps('email')}
      />
      <TextInput
        label="Predmet"
        placeholder="Zadajte predmet e-mailu"
        required
        size="sm"
        {...form.getInputProps('subject')}
      />
      <Textarea
        label="HTML obsah"
        placeholder="Zadajte HTML obsah e-mailu"
        required
        size="sm"
        minRows={4}
        rows={4}
        styles={{ input: { minHeight: '200px' } }}
        {...form.getInputProps('html')}
      />
      <Checkbox
        label="Priložiť vygenerované PDF"
        size="sm"
        {...form.getInputProps('attachPdf', { type: 'checkbox' })}
      />
      {isAdmin && (
        <Button
          variant="outline"
          size="xs"
          leftIcon={<IconMailForward size={16} />}
          onClick={sendTestEmail}
          loading={loading}
          mt="md"
        >
          Odoslať testovací e-mail
        </Button>
      )}
    </>
  );
}
